/**
 * <Seo> — the per-route head block.
 *
 * WHY THIS EXISTS
 * ---------------
 * `public/index.html` used to hardcode `<link rel="canonical" href="https://sov.ai"/>`,
 * which told Google that every route on the site — /faq included — was a
 * duplicate of the homepage. That single line was de-indexing the only content
 * page the site had. The global canonical is now gone, and canonicals are the
 * responsibility of each route via this component.
 *
 * CONSEQUENCE, READ THIS: a route that does not render <Seo> now emits NO
 * canonical at all. That is strictly better than the wrong canonical it had
 * before, but it is not the goal. Every indexable page should render <Seo>.
 *
 * API
 * ---
 *   <Seo
 *     title="Accounting Data"                  // required. Rendered through the
 *                                              // app-level titleTemplate as
 *                                              // "Accounting Data - SOVAI".
 *     description="One sentence, 50–160 chars" // required.
 *     path="/datasets/accounting-data"         // required. Route path; the
 *                                              // canonical is derived from it.
 *     image="https://sov.ai/og_image.jpg"      // optional, absolute URL.
 *     type="article"                           // optional og:type, default "website".
 *     noIndex                                  // optional. Emits noindex,follow.
 *     templated={false}                        // optional. Set false when `title`
 *                                              // is already a complete title and
 *                                              // must not get the " - SOVAI" suffix.
 *     keywords={['alternative data']}          // optional.
 *     jsonLd={node | [node, node]}             // optional structured data.
 *   />
 *
 * Notes for adopters:
 *  - `path` not a full URL. Canonicalisation (leading slash, trailing-slash strip)
 *    is handled centrally in `canonicalUrl`.
 *  - Exactly one <Seo> per rendered route. Two mounted at once will fight over
 *    the canonical, and react-helmet-async resolves that by "last one wins",
 *    which is a coin flip you do not want deciding your canonical tags.
 *  - JSON-LD builders live next door in `./datasetJsonLd`. Import from there
 *    rather than hand-writing schema objects at the call site.
 */

import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { JsonLd, SITE, canonicalUrl } from './datasetJsonLd';

export interface SeoProps {
  /** Page title. Templated to "<title> - SOVAI" unless `templated` is false. */
  title: string;
  /** Meta description. Aim for 50–160 characters. */
  description: string;
  /** Route path, e.g. "/datasets/accounting-data". Drives the canonical URL. */
  path: string;
  /** Absolute URL for og:image / twitter:image. Defaults to the site OG image. */
  image?: string;
  /** og:type. "website" for indexes, "article" for content pages. */
  type?: 'website' | 'article';
  /** Emit `noindex, follow`. Use for thin, gated or duplicate routes. */
  noIndex?: boolean;
  /** Set false when `title` is already complete and must not be suffixed. */
  templated?: boolean;
  /** Optional keyword list. */
  keywords?: string[];
  /** One or more schema.org nodes. Serialised into <script type=ld+json>. */
  jsonLd?: JsonLd | JsonLd[];
}

/**
 * JSON-LD has to be embedded as raw text inside a <script>. The one character
 * that can break out of that context is `<` (e.g. a literal "</script>" inside
 * a description), so escape it. `<` is valid JSON and parses identically.
 */
const serialiseJsonLd = (node: JsonLd): string =>
  JSON.stringify(node).replace(/</g, '\\u003c');

export const Seo: React.FC<SeoProps> = ({
  title,
  description,
  path,
  image = SITE.defaultImage,
  type = 'website',
  noIndex = false,
  templated = true,
  keywords,
  jsonLd,
}) => {
  const canonical = canonicalUrl(path);
  const nodes = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet
      // Passing titleTemplate="%s" locally opts this page out of the app-level
      // "%s - SOVAI" suffix without changing it for anyone else.
      {...(templated ? {} : { titleTemplate: '%s' })}
    >
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <meta
        name="robots"
        content={noIndex ? 'noindex, follow' : 'index, follow'}
      />
      {keywords && keywords.length > 0 ? (
        <meta name="keywords" content={keywords.join(', ')} />
      ) : null}

      {/* OpenGraph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={SITE.name} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {nodes.map((node, index) => (
        <script
          key={`ld-${index}`}
          type="application/ld+json"
          data-seo="jsonld"
        >
          {serialiseJsonLd(node)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;

// Re-exported so a page needs a single import path for the common case.
export {
  SITE,
  canonicalUrl,
  datasetUrl,
  datasetJsonLd,
  dataCatalogJsonLd,
  organizationJsonLd,
  breadcrumbJsonLd,
  webSiteJsonLd,
} from './datasetJsonLd';
export type { JsonLd } from './datasetJsonLd';
